import { NotificationType, pinConversationRulebook, useAppDispatch, useTranslation } from "@next-platform/client-common-core";
import useMyRulebooks from "@next-platform/client-common-core/rulebook/attach/useMyRulebooks";
import { HttpStatusCode } from "@next-platform/packages-ts-types";
import React, { memo, useCallback } from "react";

import { useToast } from "@/hooks";

import ModalRulebook from "./ModalRulebook";

type ConversationRulebookPickerProps = {
  conversationId: string;
  isOpen: boolean;
  onClose: () => void;
};

const isConflict = (error: unknown): boolean => (error as { status?: number } | undefined)?.status === HttpStatusCode.CONFLICT;

/**
 * Picker that pins one of the user's Rulebooks onto the current chat — the shared Rulebook picker with chat copy. The
 * pin applies at once (there's no form to save) and is final, so the HUD switches to it right away.
 */
const ConversationRulebookPicker = ({ conversationId, isOpen, onClose }: ConversationRulebookPickerProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { displayToast } = useToast();
  // Loaded only while open: the chat page shouldn't fetch the user's Rulebooks for a menu item nobody opened.
  const { rulebooks, isLoading, hasError, reload } = useMyRulebooks({ isEnabled: isOpen });

  const handleRetry = useCallback(() => {
    void reload();
  }, [reload]);

  const handleSelected = useCallback(
    (rulebookId: string) => {
      dispatch(pinConversationRulebook({ conversationId, rulebookId }))
        .unwrap()
        .then(() => displayToast({ message: t("chat:rulebook.attach.success", "Rulebook attached to this chat."), type: NotificationType.success }))
        .catch((error: unknown) =>
          displayToast({
            message: isConflict(error)
              ? t("chat:rulebook.attach.alreadyPinned", "This chat already has a Rulebook, and it can't be changed.")
              : t("chat:rulebook.attach.failed", "The Rulebook couldn't be attached. Try again."),
            type: NotificationType.error,
          }),
        );
    },
    [conversationId, dispatch, displayToast, t],
  );

  return (
    <ModalRulebook
      ownerType="conversation"
      isOpen={isOpen}
      onClose={onClose}
      rulebooks={rulebooks}
      isLoading={isLoading}
      hasError={hasError}
      onRetry={handleRetry}
      selectedRulebookId={null}
      onRulebookSelected={handleSelected}
      dataTestId="ConversationRulebookPicker"
    />
  );
};

export default memo(ConversationRulebookPicker);
